import { Button, Typography } from "antd";
import React from "react";
import type { Organization } from "../../../../api/v1";
import { leaseService } from "../../../../configs/constants";
import { GridType } from "../../../../types/grid";
import axios from "../../../../utils/axiosInceptor";
import { dateByformat } from "../../../../utils/dateHelper";
import { formatCurrency, formatDateByType, formatNumber } from "../../../libs/utils";
import { ReceiptRowActions } from "./receiptRowActions";
import { isReceiptBulkSelectionRow } from "./utils";
import { CheckOutlined } from "@ant-design/icons";

export type ReceiptActionHandler = (action: string, row: any) => void;

const convertToNormalCase = (input = "") => {
  if (!input) return "";
  if (["directDebit", "directdebit"].includes(input)) return "Direct Debit";
  if (input.indexOf("_") === -1 && input !== input.toLowerCase()) {
    return input.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (input.indexOf("_") !== -1) {
    return input.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return input.charAt(0).toUpperCase() + input.slice(1);
};

const convertPaymentTypeLabel = (input = "") => {
  if (!input) return "";
  return input
    .replace(/\d+/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const formatBeneficiaryBank = (bank: unknown): string => {
  if (bank && typeof bank === "object" && "label" in (bank as object)) {
    return String((bank as { label?: string }).label ?? "");
  }
  return bank != null && bank !== "" ? String(bank) : "";
};

const beneficiaryText = (value: unknown) =>
  value != null && String(value).trim() !== "" ? String(value).trim() : "--";

/** Receipt list API: `beneficiary.accountTitle`, `bankName`, `accountNumber`, `iban`; `owner.name`. */
const getReceiptBeneficiaryName = (data: any) => data?.beneficiary?.accountTitle ?? "--";

const getReceiptOwnerName = (data: any) => data?.owner?.name ?? "--";

const getReceiptBeneficiaryBank = (data: any) => formatBeneficiaryBank(data?.beneficiary?.bankName) || "--";

const getReceiptBeneficiaryAccount = (data: any) => data?.beneficiary?.accountNumber ?? "--";

const getReceiptBeneficiaryIban = (data: any) => data?.beneficiary?.iban ?? "--";

export const receiptColumnDefs: GridType[] = [
  { id: "receiptNo", field: "receiptNo", headerName: "Receipt No", checked: true, hide: false },
  { id: "invoiceNumber", field: "invoiceNumber", headerName: "Invoice No", checked: true, hide: false },
  { id: "erpReceiptId", field: "erpReceiptId", headerName: "ERP Receipt Id", checked: true, hide: false },
  { id: "erpTenantId", field: "erpTenantId", headerName: "ERP Customer Id", checked: true, hide: false },
  { id: "erpInvoiceNumber", field: "erpInvoiceNumber", headerName: "ERP Invoice Id", checked: true, hide: false },
  { id: "tenantName", field: "tenantDetails.tenantNameEN", headerName: "Tenant Name", checked: true, hide: false },
  { id: "property", field: "propertyDetails.propertyName", headerName: "Property", checked: true, hide: false },
  { id: "unitCode", field: "unitCode", headerName: "Unit Code", checked: true, hide: false },
  { id: "paymentMode", field: "modeOfPayment", headerName: "Payment Mode", checked: true, hide: false },
  { id: "totalAmount", field: "totalAmount", headerName: "Total Amount", checked: true, hide: false },
  { id: "receiptStatus", field: "receiptStatus", headerName: "Receipt Status", checked: true, hide: false },
  // { id: "paymentStatus", field: "chequeStatus", headerName: "Payment Status", checked: true, hide: false },
  { id: "receiptDate", field: "paidDate", headerName: "Receipt Date", checked: true, hide: false },
  { id: "clearedDate", field: "clearedDate", headerName: "Clearing Date", checked: true, hide: false },
  { id: "bouncedDate", field: "bouncedDate", headerName: "Bounced Date", checked: true, hide: false },
  { id: "deferDate", field: "deferDate", headerName: "Deferred Date", checked: true, hide: false },
  { id: "recallDate", field: "recallDate", headerName: "Recalled Date", checked: true, hide: false },
  { id: "chequeDate", field: "chequeDate", headerName: "Cheque Date", checked: true, hide: false },
  { id: "bankDetails", field: "bankName", headerName: "Bank Name", checked: true, hide: false },
  { id: "beneficiaryName", field: "beneficiaryName", headerName: "Beneficiary Name", checked: false, hide: true },
  { id: "ownerName", field: "ownerName", headerName: "Owner Name", checked: false, hide: true },
  { id: "beneficiaryBank", field: "beneficiaryBank", headerName: "Beneficiary Bank", checked: false, hide: true },
  {
    id: "beneficiaryAccount",
    field: "beneficiaryAccount",
    headerName: "Beneficiary Account",
    checked: false,
    hide: true,
  },
  { id: "beneficiaryIBAN", field: "beneficiaryIBAN", headerName: "Beneficiary IBAN", checked: false, hide: true },

  { id: "requester", field: "requesterName", headerName: "Requester", checked: true, hide: false },
  { id: "requestType", field: "requestType", headerName: "Request Type", checked: true, hide: false },
  { id: "actions", field: "action", headerName: "Actions", checked: true, hide: false },
];

const { Text } = Typography;

/** Same as invoicing `pdcColumn.tsx` — fetch signed URL and open in new tab. */
const downLoadDocument = async (id: string) => {
  const url = `${leaseService}/document/getDocument/${id}?preview=true`;

  try {
    const response = await axios.get(url, {
      headers: {
        "bussiness-unit": "AUTO",
        accept: "*/*",
        "organization-unit": "AGP",
        channel: "MOBILE",
        "accept-language": "EN",
        source: "Orion",
      },
    });

    const { data } = response;

    const link = document.createElement("a");
    link.target = "_blank";
    link.href = data?.result?.location;
    link.dispatchEvent(new MouseEvent("click"));
  } catch (error) {
    // intentionally silent — matches invoicing behavior
  }
};

/** Receipts list returns `receiptDocumentId` (documentId_…) for the PDF; same getDocument flow as invoice. */
const getReceiptDownloadDocumentId = (row: any) => row?.receiptDocumentId || row?.paymentReceiptId || "";

export const buildReceiptColumns = (
  paymentColumns: any[],
  onAction: ReceiptActionHandler,
  organization?: Organization,
  canApproveReceipts?: boolean,
) => {
  return paymentColumns.map((col: any) => {
    if (col.field === "bounceDate") {
      return {
        ...col,
        minWidth: 140,
        valueFormatter: ({ value }) => formatDateByType(value),
      };
    }
    if (col.field === "totalAmount") {
      return {
        ...col,
        minWidth: 160,
        flex: 1,
        type: "rightAligned",
        cellRendererFramework: ({ value }: any) => formatCurrency(value),
      };
    }
    if (col.field === "vat") {
      return {
        ...col,
        minWidth: 130,
        type: "rightAligned",
        cellRendererFramework: ({ value }: any) => formatNumber(value),
      };
    }
    if (col.field === "modeOfPayment") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => convertToNormalCase(data?.modeOfPayment),
      };
    }
    if (col.field === "description") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => convertPaymentTypeLabel(data?.description),
      };
    }
    if (col.field === "receiptStatus") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => <Text>{convertToNormalCase(data?.receiptStatus)}</Text>,
      };
    }
 
    if (
      col.field === "clearedDate" ||
      col.field === "bouncedDate" ||
      col.field === "deferDate" ||
      col.field === "recallDate"
    ) {
      return {
        ...col,
        minWidth: 150,
        flex: 1,
        cellRendererFramework: ({ value }: any) => (value ? dateByformat(value, "DD-MM-YYYY") : "--"),
      };
    }
    if (col.field === "chequeDate") {
      return {
        ...col,
        minWidth: 150,
        flex: 1,
        cellRendererFramework: ({ data }: any) => dateByformat(data.chequeDate, "DD-MM-YYYY") ?? "--",
      };
    }
    if (col.field === "bankName") {
      return {
        ...col,
        minWidth: 180,
        flex: 1,
        cellRendererFramework: ({ value }: any) => value || "--",
      };
    }
    if (col.field === "beneficiaryName") {
      return {
        ...col,
        minWidth: 180,
        flex: 1,
        cellRendererFramework: ({ data }: any) => beneficiaryText(getReceiptBeneficiaryName(data)),
      };
    }
    if (col.field === "ownerName") {
      return {
        ...col,
        minWidth: 180,
        flex: 1,
        cellRendererFramework: ({ data }: any) => beneficiaryText(getReceiptOwnerName(data)),
      };
    }
    if (col.field === "beneficiaryBank") {
      return {
        ...col,
        minWidth: 180,
        flex: 1,
        cellRendererFramework: ({ data }: any) => beneficiaryText(getReceiptBeneficiaryBank(data)),
      };
    }
    if (col.field === "beneficiaryAccount") {
      return {
        ...col,
        minWidth: 180,
        flex: 1,
        cellRendererFramework: ({ data }: any) => beneficiaryText(getReceiptBeneficiaryAccount(data)),
      };
    }
    if (col.field === "beneficiaryIBAN") {
      return {
        ...col,
        minWidth: 200,
        flex: 1,
        cellRendererFramework: ({ data }: any) => beneficiaryText(getReceiptBeneficiaryIban(data)),
      };
    }

    if (col.field === "requesterName") {
      return {
        ...col,
        minWidth: 150,
        flex: 1,
        cellRendererFramework: ({ value }: any) => value || "--",
      };
    }
    if (col.field === "requestType") {
      return {
        ...col,
        minWidth: 150,
        flex: 1,
        cellRendererFramework: ({ value }: any) => convertToNormalCase(value) || "--",
      };
    }
    if (col.field === "paidDate") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => (data?.paidDate ? dateByformat(data.paidDate, "DD-MM-YYYY") : ""),
      };
    }
    if (col.field === "receiptNo") {
      return {
        ...col,
        headerCheckboxSelection: true,
        checkboxSelection: (params: any) => isReceiptBulkSelectionRow(params.data),
        cellRendererFramework: ({ data }: any) => {
          const value = data?.receiptNo;

          const docId = getReceiptDownloadDocumentId(data);
          if (!value) return "";
          if (!docId) return `Rec_${value}`;
          return (
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {data?.isCleared && <CheckOutlined style={{ color: "green" }} />}

              <Button
                type="link"
                className="ag-primary"
                style={{ padding: 0 }}
                title={`Click to download receipt no ${value}`}
                onClick={() => downLoadDocument(docId)}
              >
                {`Rec_${value}`}
              </Button>
            </span>
          );
        },
      };
    }
    if (col.field === "invoiceNumber") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => data?.invoiceNo || "",
      };
    }
    if (col.field === "erpReceiptId") {
      return {
        ...col,
        minWidth: 150,
        cellRendererFramework: ({ data }: any) => data?.erpReceiptId || "--",
      };
    }
    if (col.field === "erpTenantId") {
      return {
        ...col,
        minWidth: 150,
        cellRendererFramework: ({ data }: any) => data?.erpTenantId || "--",
      };
    }
    if (col.field === "erpInvoiceNumber") {
      return {
        ...col,
        minWidth: 180,
        cellRendererFramework: ({ data }: any) => {
          const value = data?.erpInvoiceNumber;
          if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "--";
          return value || "--";
        },
      };
    }
    if (col.field === "propertyDetails.propertyName") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => data?.propertyDetails?.propertyName || data?.property || "",
      };
    }
    if (col.field === "tenantDetails.tenantNameEN") {
      return {
        ...col,
        cellRendererFramework: ({ data }: any) => data?.tenantDetails?.tenantNameEN || data?.tenantName || "",
      };
    }
    if (col.field === "action" || col.field === "actions" || col.id === "actions" || col.headerName === "Actions") {
      return {
        ...col,
        maxWidth: 220,
        filter: false,
        pinned: "right",
        cellStyle: { textAlign: "center" },
        cellRendererFramework: (params: any) => (
          <ReceiptRowActions
            row={params?.data}
            onAction={onAction}
            organization={organization}
            canApproveReceipts={canApproveReceipts}
          />
        ),
      };
    }
    return col;
  });
};
